const _ = require("lodash");
const Font = require("../../util/Font");
const Common = require("./CommonHelpers");
const World3D = require("./World3D");
const {V: V2, V3} = require("./VectorDraw");
const colors = require("../../util/ColorUtil").getDiagramColors();
const {findAllEdges} = require("./VOverlap");

// Misc helpers

const hiddenEdgeStyle = {
	opacity: 0.5,
	lineDash: [3],
};

const isVector = function(x) {
	return Array.isArray(x) || x instanceof V3;
};

// Full documentation for each function is in the batex guide.

/**
 * Was getting "Declaration emit for this file requires using private name".
 * This `type` annotation is a workaround. See: https://stackoverflow.com/questions/60009092/declaration-will-not-emit-due-to-private-name-usage
 * @type {unknown}
 */
const ShapeVolume = function(ctx, x, y, w, h, settings) {
	if (!(this instanceof ShapeVolume)) {
		return new ShapeVolume(ctx, x, y, w, h, settings);
	}
	let {
		// World settings
		lookFrom = [12.5, -12.5, 8.5],
		lookAt = [5, 5, 5],
		fov = Math.PI / 4,
	} = settings || {};
	const {
		// Helper world settings that transform the above, more basic ones.
		zoom = 1,
		reducePerspective = 20,
		// Other settings
		isFullDiagram = true,
		explicitDrawOrder = false,
	} = settings || {};

	this.ctx = ctx;
	if (isFullDiagram) {
		ctx.setSize(w, h);
	}
	ctx.__addEndHook(() => this.draw());

	lookFrom = V3(lookFrom);
	lookAt = V3(lookAt);
	const origFOV = fov;
	let totalZoom = 1 / zoom;
	if (reducePerspective) {
		fov /= reducePerspective;
		totalZoom *= Math.tan(origFOV / 2) / Math.tan(fov / 2);
	}
	lookFrom = lookFrom.interpolate(lookAt, totalZoom);
	this.world = new World3D({
		lookFrom,
		lookAt,
		fov,
		canvasRect: [x, y, w, h],
		explicitDrawOrder,
	});
};

ShapeVolume.prototype.draw = function() {
	this.world.draw(this.ctx);
};

ShapeVolume.prototype.solid = function(faces, settings) {
	const {
		interior,
		fill = null,
		stroke = null,
		strokeWidth = null,
		showHiddenEdges = !fill,
	} = settings || {};
	faces = faces.map(vs => vs.map(v => V3(v)));

	const faceIsBackfaceList = faces.map(vs =>
		this.world.isBackface({vs, int: interior})
	);
	const edgeData = findAllEdges(faces);
	if (stroke) {
		edgeData.forEach(e => {
			const hidden = e.faceInds.every(i => faceIsBackfaceList[i]);
			const worldFace = {
				vs: e.vs,
				stroke,
				strokeWidth,
			};
			if (hidden && showHiddenEdges) {
				_.extend(worldFace, hiddenEdgeStyle);
				this.world.addFace(worldFace);
			} else if (!hidden) {
				this.world.addFace(worldFace);
			}
		});
	}
	if (fill) {
		faces.forEach(vs => {
			this.world.addFace({vs, int: interior, fill});
		});
	}
	if (!stroke && !fill) {
		throw new Error(
			"Called ShapeVolume.solid with no stroke or fill, which would do nothing."
		);
	}
};

ShapeVolume.prototype.face = function(vs, settings) {
	const {fill = null, stroke = null, strokeWidth = null} = settings || {};
	vs = vs.map(v => V3(v));

	const edgeData = findAllEdges([vs]);
	if (stroke) {
		edgeData.forEach(e => {
			this.world.addFace({vs: e.vs, stroke, strokeWidth});
		});
	}
	if (fill) {
		this.world.addFace({vs, fill});
	}
	if (!stroke && !fill) {
		throw new Error(
			"Called ShapeVolume.face with no stroke or fill, which would do nothing."
		);
	}
};

ShapeVolume.prototype.edge = function(v1, v2, settings) {
	const {stroke = colors.defaultBlack, strokeWidth = null, asHidden = false} =
		settings || {};
	v1 = V3(v1);
	v2 = V3(v2);

	if (!stroke) {
		throw new Error(
			"Called ShapeVolume.edge with no stroke, which would do nothing."
		);
	}
	const worldFace = {vs: [v1, v2], stroke, strokeWidth};
	if (asHidden) {
		_.extend(worldFace, hiddenEdgeStyle);
	}
	this.world.addFace(worldFace);
};

ShapeVolume.prototype.dot = function(v, settings) {
	const {
		color = colors.defaultBlack,
		// large = false,
		label = null,
		labelDir = Common.D.N,
		labelFontScale = null,
		debugFont = null,
	} = settings || {};
	v = V3(v);

	this.world.addPostDraw(ctx => {
		ctx.save();
		ctx.bp();
		ctx.fillStyle = color;
		const p = this.world.toCanvas(v);
		const radius = ctx.lineWidth * 3;
		ctx.arc(p[0], p[1], radius, 0, 2 * Math.PI);
		ctx.fill();
		ctx.restore();
	});
	if (label) {
		this.label(v, label, {
			color,
			dir: labelDir,
			fontScale: labelFontScale,
			debugFont,
		});
	}
};

ShapeVolume.prototype.label = function(...args) {
	let vs, text, settings;
	if (args[0] && isVector(args[0][0])) {
		// [v...], text, settings
		vs = args[0];
		text = args[1];
		settings = args[2] || {};
	} else {
		// v..., text, settings
		vs = [];
		for (let i = 0; isVector(args[i]); i += 1) {
			vs.push(args[i]);
		}
		text = args[vs.length];
		settings = args[vs.length + 1] || {};
	}
	// End argument juggling.
	vs = vs.map(v => V3(v));
	const {
		color = colors.defaultBlack,
		backColor = null,
		dir = vs.length > 2 ? null : Common.D.N,
		fontScale = null,
		debugFont = null,
	} = settings;

	const labelPos = V3.average(...vs);
	this.world.addPostDraw(ctx => {
		const p = this.world.toCanvas(labelPos);
		ctx.save();
		ctx.fillStyle = color;
		if (fontScale) {
			ctx.font = Font.scaleSize(ctx.font, fontScale);
		}
		if (debugFont) {
			ctx.font = debugFont;
		}
		Common.label(ctx, text, p[0], p[1], dir || [0, 0], {backColor});
		ctx.restore();
	});
};

// Returns {p1, p2, normal}, all V2 vectors giving canvas coordinates. world is
// a World3D object. v1 and v2 are V3 vectors. perpDist is the distance of p1
// and p2 from where v1 and v2 are drawn, with negative perpDist meaning to
// choose the p1 and p2 with lower y.
const computeEdgeMarkingEndpoints = function(world, v1, v2, perpDist) {
	let p1 = V2(world.toCanvas(v1));
	let p2 = V2(world.toCanvas(v2));
	let normal = p2
		.sub(p1)
		.rotateDeg(90)
		.unit();
	if (normal[1] < 0 || normal[0] === -1) {
		normal = normal.neg();
	}
	p1 = p1.add(normal.scale(perpDist));
	p2 = p2.add(normal.scale(perpDist));
	return {p1, p2, normal};
};

ShapeVolume.prototype.edgeLabel = function(v1, v2, text, settings = {}) {
	const {
		color = colors.defaultBlack,
		backColor = null,
		below = false,
		dir = null,
		vertical = false,
		fontScale = null,
		debugFont = null,
	} = settings;
	this.world.addPostDraw(ctx => {
		ctx.save();
		ctx.fillStyle = color;
		if (fontScale) {
			ctx.font = Font.scaleSize(ctx.font, fontScale);
		}
		if (debugFont) {
			ctx.font = debugFont;
		}
		const perpDist = (below ? 1 : -1) * (Font.getSize(ctx) / 2 + 4);
		const {p1, p2} = computeEdgeMarkingEndpoints(this.world, v1, v2, perpDist);
		const d = p2.sub(p1);
		const m = p1.mid(p2);
		ctx.translate(m[0], m[1]);
		if (!vertical) {
			ctx.rotate(Math.atan(d[1] / d[0]));
		}
		const fudge = Math.abs(perpDist / 8);
		Common.label(ctx, text, 0, 0, dir || [0, 0], {
			backColor,
			backColorWidthPadding: 3 * fudge,
		});
		ctx.restore();
	});
};

ShapeVolume.prototype.measureLine = function(v1, v2, text, settings) {
	// TODO: more sharing with the ShapeArea version?
	const {
		color = colors.defaultBlack,
		strokeWidth = null,
		below = false,
		labelColor = color,
		labelBackColor = colors.white,
		labelDir = null,
		labelVertical = false,
		labelFontScale = null,
		font = null,
	} = settings;
	v1 = V3(v1);
	v2 = V3(v2);
	this.world.addPostDraw(ctx => {
		ctx.save();
		ctx.strokeStyle = color;
		if (strokeWidth) {
			ctx.lineWidth = strokeWidth;
		}
		const perpDistMagnitude = text ? Font.getSize(ctx) / 2 + 4 : 10;
		const perpDist = (below ? 1 : -1) * perpDistMagnitude;
		const {p1, p2, normal} = computeEdgeMarkingEndpoints(
			this.world,
			v1,
			v2,
			perpDist
		);
		const markSize = 6 * ctx.lineWidth;
		const markV = normal.scale(markSize);
		ctx
			.bp()
			.M(p1.sub(markV))
			.L(p1.add(markV))
			.M(p1)
			.L(p2)
			.M(p2.sub(markV))
			.L(p2.add(markV))
			.stroke();
		ctx.restore();
	});
	if (text) {
		this.edgeLabel(v1, v2, text, {
			color: labelColor,
			backColor: labelBackColor,
			dir: labelDir,
			vertical: labelVertical,
			fontScale: labelFontScale,
			below,
			font,
		});
	}
};

//
// Convenience wrappers for .solid
//

ShapeVolume.prototype.prism = function(baseVs, apex, settings) {
	const {
		fill = null,
		baseFill = null,
		shiftedBaseFill = null,
		lateralFills = null,
		stroke = null,
		strokeWidth = null,
		showHiddenEdges = !fill,
	} = settings || {};
	baseVs = baseVs.map(v => V3(v));
	if (typeof apex === "number") {
		apex = V3.faceNormal(...baseVs).toMagnitude(apex);
	}

	const shiftedBaseVs = baseVs.map(v => v.add(apex));
	const faces = [baseVs, shiftedBaseVs];
	// Base face
	const finalBaseFill = baseFill || fill;
	if (finalBaseFill) {
		this.world.addFace({vs: baseVs, fill: finalBaseFill});
	}
	// Shifted base face
	const finalShiftedBaseFill = shiftedBaseFill || fill;
	if (finalShiftedBaseFill) {
		this.world.addFace({vs: shiftedBaseVs, fill: finalShiftedBaseFill});
	}
	// Lateral faces
	for (let i = 0; i < baseVs.length; i += 1) {
		const nextI = (i + 1) % baseVs.length;
		const vs = [
			baseVs[i],
			baseVs[nextI],
			shiftedBaseVs[nextI],
			shiftedBaseVs[i],
		];
		const lateralFill = (lateralFills && lateralFills[i]) || fill;
		if (lateralFill) {
			this.world.addFace({vs, fill: lateralFill});
		}
		faces.push(vs);
	}
	if (stroke) {
		const interior = V3.average(...baseVs.concat(shiftedBaseVs));
		this.solid(faces, {interior, stroke, strokeWidth, showHiddenEdges});
	}
};

ShapeVolume.prototype.box = function(v1, v2, settings) {
	const {
		fill = null,
		topFill = null,
		bottomFill = null,
		leftFill = null,
		rightFill = null,
		frontFill = null,
		backFill = null,
		stroke = null,
		strokeWidth = null,
		showHiddenEdges = !fill,
	} = settings || {};
	const [minX, minY, minZ] = V3.min([v1, v2]);
	const [maxX, maxY, maxZ] = V3.max([v1, v2]);
	const baseFace = [
		V3(minX, minY, minZ),
		V3(maxX, minY, minZ),
		V3(maxX, maxY, minZ),
		V3(minX, maxY, minZ),
	];
	this.prism(baseFace, maxZ - minZ, {
		fill,
		baseFill: bottomFill,
		shiftedBaseFill: topFill,
		lateralFills: [frontFill, rightFill, backFill, leftFill],
		stroke,
		strokeWidth,
		showHiddenEdges,
	});
};

ShapeVolume.prototype.pyramid = function(baseVs, apex, settings) {
	const {
		fill = null,
		baseFill = null,
		lateralFills = null,
		stroke = null,
		strokeWidth = null,
		showHiddenEdges = !fill,
	} = settings || {};
	baseVs = baseVs.map(v => V3(v));
	apex = V3(apex);

	const faces = [baseVs];
	// Base face
	const finalBaseFill = baseFill || fill;
	if (finalBaseFill) {
		this.world.addFace({vs: baseVs, fill: finalBaseFill});
	}
	// Lateral faces
	for (let i = 0; i < baseVs.length; i += 1) {
		const nextI = (i + 1) % baseVs.length;
		const vs = [baseVs[i], baseVs[nextI], apex];
		const lateralFill = (lateralFills && lateralFills[i]) || fill;
		if (lateralFill) {
			this.world.addFace({vs, fill: lateralFill});
		}
		faces.push(vs);
	}
	if (stroke) {
		const interior = V3.average(...baseVs.concat([apex]));
		this.solid(faces, {interior, stroke, strokeWidth, showHiddenEdges});
	}
};

//
// V3 aids
//

ShapeVolume.prototype.makeRegular = function(n, v1, v2, v3, flip = false) {
	v1 = V3(v1);
	v2 = V3(v2);
	let normal = V3(v3).faceNormal(v1, v2);
	if (normal.magnitude() === 0) {
		throw new Error("Called makeRegular for collinear points");
	}
	if (flip) {
		normal = normal.neg();
	}
	const ang = (Math.PI * (n - 2)) / n;
	const coordinates = [v1, v2];
	for (let i = 2; i < n; i++) {
		const prev = coordinates[i - 1];
		const prevPrev = coordinates[i - 2];
		coordinates.push(V3.rotateNormal(prevPrev, ang, normal, prev));
	}
	return {
		vs: coordinates,
		center: V3.average(...coordinates),
	};
};

//
// Debug
//

ShapeVolume.prototype.debugGrid = function(minX, minY, maxX, maxY, step = 1) {
	minX = Math.floor(minX / step) * step;
	minY = Math.floor(minY / step) * step;
	maxX = Math.ceil(maxX / step) * step;
	maxY = Math.ceil(maxY / step) * step;
	for (let x = minX; x <= maxX; x += step) {
		this.world.addFace({
			vs: [V3(x, minY, -0.001), V3(x, maxY, -0.001)],
			stroke: colors.mediumLightGray,
			priority: -1,
		});
	}
	for (let y = minY; y <= maxY; y += step) {
		this.world.addFace({
			vs: [V3(minX, y, -0.001), V3(maxX, y, -0.001)],
			stroke: colors.mediumLightGray,
			priority: -1,
		});
	}
};

ShapeVolume.prototype.debugFlag = function(v) {
	v = V3(v);
	const groundV = V3(v[0], v[1], 0);
	this.edge(groundV, v, {stroke: "#0000FF", strokeWidth: 3});
	this.dot(v, {
		color: "#FF0000",
		large: true,
		label: v.join(","),
		labelDir: [0, -2.5],
		debugFont: "10px sans-serif",
	});
};

// Allows other fake 3D helpers to make use of this variable.
ShapeVolume.hiddenEdgeStyle = hiddenEdgeStyle;

module.exports = ShapeVolume;
